import { ofType } from 'redux-observable';
import { concat, of, takeUntil } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';

import { CUSTOM_TAG, CUSTOM_TAG_VALUE, SYSTEM_TAG_IAB } from '../../../common/constants/label';
import { CACHE_AND_SERVICES } from '../../../common/constants/playlists';
import {
  FILTER_CONFIG_DURATION,
  MINIMUM_LANGUAGE_COUNT,
  QUERY_DURATION_KEY,
  QUERY_LANGUAGE_KEY,
  QUERY_SEARCH_KEY,
  QUERY_SELECTED_CHIP_IAB_KEY,
  QUERY_SELECTED_CHIP_KEY,
  QUERY_SELECTED_CHIP_TAG_KEY,
  QUERY_TIME_KEY,
  REQUEST_SIZE_SEARCH_ITEMS,
} from '../../../common/constants/queryParams';
import { SEARCH_ENDPOINT_URL } from '../../../common/constants/request';
import { SORT_DESC } from '../../../common/constants/sorting';

import { getMainPlayerWidget } from '../../../common/components/Player/helpers';
import { RootContainers } from '../../../common/helpers';
import { getVideoEntityTypes } from '../../../common/helpers/chips';
import { dateSubtract } from '../../../common/helpers/dateTime';
import { offset, scrollYByElement } from '../../../common/helpers/dom';
import logger from '../../../common/helpers/logger';
import { includeFeatures, safetyJsonParse, tmPostRequest } from '../../../common/helpers/request';
import { getFormattedVideo } from '../../../common/helpers/video';
import * as commonSelectors from '../../../common/redux/selectors/common';
import * as searchSelectors from '../selectors/search';
import {
  clearSearchResultAction,
  errorSearchRequestAction,
  searchRequestAction,
  searchRequestEventAction,
  setSearchQueryAction,
  setSearchRequestAction,
} from '../slices/search';

export default (action$, state$) =>
  action$.pipe(
    ofType(searchRequestAction.type),
    filter(({ payload: { query } }) => !!query[QUERY_SEARCH_KEY]),
    mergeMap(({ payload }) => {
      const querySearch = payload.query[QUERY_SEARCH_KEY];
      const queryLanguage = payload.query[QUERY_LANGUAGE_KEY];
      const time = payload.query[QUERY_TIME_KEY];
      const duration = payload.query[QUERY_DURATION_KEY];

      const id = commonSelectors.idSelector(state$.value);
      const player = commonSelectors.playerSelector(state$.value);
      const publisher = commonSelectors.publisherSelector(state$.value);
      const languages = commonSelectors.languagesSelector(state$.value);
      const dsTags = commonSelectors.dsTagsSelector(state$.value);
      const deepSearch = commonSelectors.deepSearchSelector(state$.value);
      const storedPlaylist = searchSelectors.playlist(state$.value);
      const tags = commonSelectors.tagsSelector(state$.value);
      const tagTypes = commonSelectors.tagTypesSelector(state$.value);

      const { [QUERY_SELECTED_CHIP_TAG_KEY]: queryChip, [QUERY_SELECTED_CHIP_IAB_KEY]: queryChipIabIds } =
        safetyJsonParse(payload.query[QUERY_SELECTED_CHIP_KEY]);

      let chipFilters;
      if (queryChip) {
        const isIabExist = tagTypes.includes(SYSTEM_TAG_IAB);

        chipFilters = {
          query: queryChip,
          types:
            tagTypes.length > (isIabExist ? 1 : 0) ? tagTypes.filter((type) => type !== SYSTEM_TAG_IAB) : undefined,
          iabIds: isIabExist ? queryChipIabIds : undefined,
        };
      }

      const describeTypes = dsTags
        .filter((item) => deepSearch || (!deepSearch && item === CUSTOM_TAG_VALUE))
        .map((item) => (item === CUSTOM_TAG_VALUE ? CUSTOM_TAG : item));

      const ranges = [];

      let languageFilters;
      if (languages.length && languages.length < MINIMUM_LANGUAGE_COUNT) {
        languageFilters = languages[0]?.value && [languages[0].value];
      } else if (languages.length >= MINIMUM_LANGUAGE_COUNT && !queryLanguage) {
        languageFilters = languages.filter((language) => !!language.value).map((language) => language.value);
      } else if (languages.length >= MINIMUM_LANGUAGE_COUNT && queryLanguage) {
        const isQueryLanguageExist = languages.find((language$) => language$.value === queryLanguage);
        languageFilters = isQueryLanguageExist && [queryLanguage];
      }

      if (time) {
        const dateNow = new Date();

        ranges.push({
          from: dateSubtract(dateNow, -1, time).getTime(),
          to: dateNow.getTime(),
          of: 'videoCreationDate',
        });
      }

      if (duration) {
        const { from, to } = FILTER_CONFIG_DURATION.find((item) => item.value === duration);

        ranges.push({
          from,
          to,
          of: 'videoLength',
        });
      }

      const startTime = Date.now();

      const stream$ = tmPostRequest({
        url: SEARCH_ENDPOINT_URL,
        body: {
          publisherId: publisher.slug,
          widgetId: player.name,
          query: querySearch,
          vhId: id,
          from: payload.isAddRequest ? storedPlaylist.length : 0,
          size: REQUEST_SIZE_SEARCH_ITEMS,
          sort: {
            by: 'relevancy',
            order: SORT_DESC,
          },
          describe: !!describeTypes.length,
          describeTypes: describeTypes.length ? describeTypes : undefined,
          cacheUsage: CACHE_AND_SERVICES,
          ranges: ranges.length ? ranges : null,
          distributionIds: payload.distributionIds,
          filterIds: payload.filterIds,
          videoEntityTypes: tags ? getVideoEntityTypes(tagTypes) : undefined,
          chipFilters,
          languageFilters,
          ...includeFeatures(state$.value),
        },
      }).pipe(
        map(({ status, response }) => {
          const payloadRes = {
            totalCount: payload.isAddRequest ? storedPlaylist.length : 0,
            playlist: payload.isAddRequest ? storedPlaylist : [],
            playerPropsPassThrow: {},
          };

          if (status === 200) {
            const {
              totalCount: requestTotalCount,
              data: { playlist, errors, ...playerPropsPassThrow },
            } = response;

            payloadRes.totalCount = playlist ? requestTotalCount : payloadRes.playlist.length;
            payloadRes.playerPropsPassThrow = playerPropsPassThrow;

            if (playlist?.length) {
              payloadRes.playlist = payloadRes.playlist.concat(
                playlist.map((video) => {
                  const video$ = getFormattedVideo(video, {
                    signedUrlExp: playerPropsPassThrow.signedUrlExp,
                  });

                  return {
                    ...video$,
                    describe: video$.describe.filter((item) => dsTags.includes(item.type)),
                  };
                }),
              );
            }
          }

          if (!payload.isAddRequest) {
            scrollYByElement(window, offset(RootContainers.getRootContainer()).top);
          }
          const LWP = getMainPlayerWidget();

          LWP.sendStatsPerformWS(querySearch, Date.now() - startTime, payloadRes.totalCount);

          return setSearchRequestAction(payloadRes);
        }),
        catchError((error) => {
          logger.error(
            'Error of search request',
            'src/modules/Search/redux/epics/search.js',
            SEARCH_ENDPOINT_URL,
            error,
          );

          return of(errorSearchRequestAction());
        }),
        takeUntil(action$.pipe(ofType(clearSearchResultAction.type))),
      );

      return concat(of(setSearchQueryAction(payload.query)), of(searchRequestEventAction()), stream$);
    }),
  );
